/**
 * The presentational shells private to this feature — two layout pieces, the four
 * designed states over a query, and the two notices this chapter needs because parts of
 * it are still fixture-backed.
 *
 * They are here rather than in `src/primitives/` for the reason `CLAUDE.md` §2.2
 * gives: a component two chapters need is *promoted*, and promoting is a conversation
 * rather than a unilateral move. ch00 and ch03 have near-identical shells in their own feature
 * directories, and that duplication is the visible cost of the rule — the alternative,
 * a cross-feature import, is a build failure by design so that copying is not the easy
 * path to a shared component.
 *
 * Both obey the same rules as everything else: no colour literal (design/09 §12.1),
 * logical properties only (G6), a catalogue key for every user-visible string (G6 test
 * *a*), and the numeric face right-aligned and column-aligned for every number
 * (design/09 §12.5).
 */

import { useTranslation } from 'react-i18next';

import type { MarkdownProvenance } from '@/api/generated';

import type { AbsentMeaning } from '@/primitives';
import { AboutPopover, EmptyState, KpiLinkAbove, KpiLinkAnchor, StatusIndicator, useKpiLink } from '@/primitives';
import { inseasonListRuns } from '@/api/generated';
import { useApiCollection } from '@/api';

import { useEligibleRunIds, type InSeasonRunScope } from './default-run';
import { displayNumberFormat } from '@/lib/format';

/**
 * `Panel` is the shared one now — `src/planning/panel.tsx`, ADR 9006.
 *
 * Re-exported from here rather than removed, so that the screens in this chapter keep
 * importing it from their own directory.
 *
 * THIS chapter's copy is the reason the promotion happened. It spelled its heading id
 * `launch-panel-${titleKey}` — a copy-paste out of ch03 that shipped, so every in-season
 * panel named itself with another chapter's prefix and two panels sharing a `titleKey`
 * across the two chapters would have collided on a document id. Nothing about that was
 * visible from inside this file.
 */
export { Panel, type PanelProps } from '@/planning';

/**
 * What `null` means in this chapter's figures and cells, by default.
 *
 * `Measure` and `NumericCell` require an `absent` meaning with no default (ADR 0012), and
 * most of this chapter's absences have the same cause: the contract does not publish the
 * field for this row. A shared constant keeps that one sentence in one place; a figure
 * whose absence has a NARROWER cause passes its own `reasonKey` beside this label rather
 * than reusing it, because "not published" and "published as null because the partition
 * was never counted" send a planner to different people.
 */
export const ABSENT_MEASURE: AbsentMeaning = { labelKey: 'inseason.absent.notMeasured' };



export interface FigureProps {
  readonly labelKey: string;
  readonly value: number;
  /** ICU key for the unit, shown beside the value rather than inside it (§4.3). */
  readonly unitKey: string;
  /** ICU values for the unit — a money unit names the row's own currency (G5). */
  readonly unitValues?: Readonly<Record<string, string | number>>;
  /** Fraction digits. Declared per figure, because a count and a rate round differently. */
  readonly fractionDigits?: number;
  /** What the figure really is — definition, source, limits (DEC-111, ADR 9047): an (i) after the label. */
  readonly descriptionKey?: string;
  readonly descriptionValues?: Readonly<Record<string, string | number>>;
  /** The list behind the figure (ADR 9069): the whole figure opens it. It must total `value`. Absent: no link. */
  readonly href?: string;
}

/**
 * One labelled number.
 *
 * design/09 §7.5 is explicit that a single KPI is a stat tile and not a dial, which is
 * why this is a figure with a value and a unit — the charting wrapper has no API for a
 * gauge at all (G2 test *c*).
 */
export function Figure({
  labelKey, value, unitKey, unitValues, fractionDigits = 0, descriptionKey, descriptionValues, href,
}: FigureProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const link = useKpiLink(href);
  const formatted = displayNumberFormat(i18n.language, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
  return (
    <div className={`flex flex-col gap-1${link.rootClass === '' ? '' : ` ${link.rootClass}`}`}>
      <span className="flex items-center gap-1">
        <span {...link.labelProps} className="text-caption text-ink-secondary">{t(labelKey)}</span>
        {descriptionKey !== undefined && (
          <KpiLinkAbove link={link}>
            <AboutPopover
              aboutKeys={[descriptionKey]}
              title={t(labelKey)}
              size="sm"
              {...(descriptionValues === undefined ? {} : { values: descriptionValues })}
            />
          </KpiLinkAbove>
        )}
      </span>
      <KpiLinkAnchor link={link}>
        <span {...link.valueProps} className="flex items-baseline gap-1">
          <span className="text-numeric font-numeric tabular-nums text-ink-primary">
            {formatted}
          </span>
          <span className="text-caption text-ink-secondary">{t(unitKey, unitValues ?? {})}</span>
        </span>
      </KpiLinkAnchor>
    </div>
  );
}

// `AsyncRegion` and `ProblemNotice` used to live here. They now live in
// `src/planning/async.tsx` (`docs/adr/0015`) and are imported from `@/planning`. This
// chapter's permission branch survived the promotion: it is now every chapter's.

// ── the two honest notices ───────────────────────────────────────────────────


/**
 * The read is bounded by a run and the URL does not name one.
 *
 * A designed state rather than an empty table, because "no run selected" and "this run
 * has no proposals" are different findings and the second one is a decision. See
 * `scope.ts` for why there is no default run.
 */
export function ScopeRequired(
  { titleKey, bodyKey }: { readonly titleKey: string; readonly bodyKey: string },
): React.JSX.Element {
  // The title is the caller's. It was fixed to "Name a cycle run" for every use, so the
  // D2 evidence panel said "Name a cycle run" over a sentence asking for a DATE — audit v5
  // N-11's "No gate is registered" followed by "Name a cycle run".
  return <EmptyState titleKey={titleKey} bodyKey={bodyKey} />;
}

/**
 * A panel bounded by a cycle run, with none chosen — audit v5 N-02.
 *
 * It used to say "add a run to the address bar": a request no planner can act on. The page
 * now carries `CycleRunPicker` at its head, so this panel says only what is true of it:
 *
 * - runs exist, but none the page may open by itself (DEC-99, `./default-run.tsx`) → choose one
 *   above. A run of the page's `scope` is opened for the planner, so this says something only
 *   when there is none of that kind, or no `scope` to open by (Simulate with no stage);
 * - a run of the kind exists → nothing: it is being written into the URL;
 * - no run recorded → nothing. The picker states that absence once, for the page; each run
 *   panel repeating it is G-07's "one absence, one block" broken in reverse;
 * - the directory still loading, or failed → nothing either; the picker owns that state.
 *
 * Same reads as the picker, same query keys, so no request is added.
 */
export function RunRequired({ bodyKey, scope = 'any' }: {
  readonly bodyKey: string;
  /** The kind of run the page opens by itself — the picker's `scope`. */
  readonly scope?: InSeasonRunScope | null;
}): React.JSX.Element | null {
  const runs = useApiCollection(inseasonListRuns, { query: {} });
  const eligible = useEligibleRunIds(scope, true);
  if (runs.data === undefined || runs.data.length === 0) return null;
  if (eligible === undefined || eligible.length > 0) return null;
  return <EmptyState titleKey="inseason.scope.runRequiredTitle" bodyKey={bodyKey} />;
}

/**
 * Which inputs behind a solved price path were reconstructed rather than measured.
 *
 * §4.4's degrading gap ③ is explicit that a reconstructed input "must be stated
 * plainly on every output, or the business will read it as a margin claim". The
 * platform sends the provenance per input for exactly that reason, so this
 * renders it per input too: an option with a measured landed cost and a
 * reconstructed baseline is a stronger answer than one with neither, and a single
 * "interim" badge would throw that away.
 *
 * Nothing is shown when every input was measured. A notice that appeared on a
 * fully measured answer would train a planner to ignore it on the answers where
 * it matters.
 *
 * **The landed cost is a standing estate condition, and saying so is what keeps this
 * notice readable.** All 89,066 Colombian landed costs carry `UNKNOWN` provenance —
 * `DW_CO_ARTICLE_COST_AND_PRICE_V` is a single open-ended September-2026 snapshot with no
 * history and with FOB, freight and duty null — so `unit_cost` degrades on *every* real
 * read and this notice fires on essentially every answer. A warning that always fires is
 * a warning nobody reads, and the fix is not to suppress it: a contribution figure built
 * on a reconstructed cost is exactly the "margin claim" §4.4's degrading gap ③ warns
 * about. So the two cases are distinguished. When the landed cost is the ONLY
 * reconstructed input the notice names the estate-wide cause, which is a fact about the
 * cost feed rather than about this option and is actionable in a different repository.
 * When something else degraded too, this option's answer is weaker than the estate's
 * baseline and the general sentence says which inputs.
 */
export function ProvenanceNotice(
  { provenance }: { readonly provenance: MarkdownProvenance },
): React.JSX.Element | null {
  const { t } = useTranslation();
  if (provenance.is_fully_measured) return null;
  // A lookup of literal keys rather than an interpolated family: the i18n coverage tool
  // enumerates an interpolated family only from its own registry, which is outside this
  // chapter's namespace, and a literal is greppable besides.
  const derived = ([
    ['inseason.provenance.input.listPrice', provenance.list_price],
    ['inseason.provenance.input.baseline', provenance.baseline_units_per_week],
    ['inseason.provenance.input.terminalTarget', provenance.terminal_target_units],
    ['inseason.provenance.input.unitCost', provenance.unit_cost],
    ['inseason.provenance.input.elasticity', provenance.elasticity],
  ] as const).filter(([, value]) => value !== 'MEASURED').map(([key]) => key);
  const costOnly =
    derived.length === 1 && derived[0] === 'inseason.provenance.input.unitCost';
  return (
    <div className="border-hairline bg-surface-sunken flex flex-col gap-1 rounded border p-3">
      <StatusIndicator
        level="warning"
        labelKey={costOnly ? 'inseason.provenance.costLabel' : 'inseason.provenance.label'}
      />
      <p className="text-caption text-ink-secondary max-w-prose">
        {costOnly
          ? t('inseason.provenance.costBody')
          : t('inseason.provenance.body', {
            inputs: derived.map((key) => t(key)).join(', '),
          })}
      </p>
    </div>
  );
}
